<template lang="pug">
.selections-page
  Loading(
    v-model:active="selectionsPageLoading"
    :is-full-page="true"
    :opacity="1"
  )
  ConfirmDialog(
    :breakpoints="{'3840px': '40vw', '960px': '70vw', '750px': '85vw'}"
  )
  .cards-container(v-if="!selectionsPageLoading && settingsLoaded")
    Card.selections-card
      template(#header)
        .title(v-safe-html="$t('selections.title')")
        .title 
      template(#content)
        .text {{ $t('selections.initialText.1') }}
          ul(v-safe-html="$t('selections.initialText.list')")
        .text {{ $t('selections.initialText.2') }}
        SelectionsButtons.selector(:items="formTypeOptions" v-model="selectedForm" )
    div
      component(:is="formComponent")
    .submit-button-wrapper
      Button.submit-button(
        :disabled="!termsAccepted || (userUsesMPRN && !fileUploaded) || userUsesMPRN === undefined || !addressRequiredAndValid"
        @click="submitForm()"
        :label="$t('selections.submitButton')"
      )
</template>

<script lang="ts">
import { WritableComputedRef, computed, defineComponent } from "vue";
import Card from "primevue/card";
import Button from "primevue/button";
import ConfirmDialog from "primevue/confirmdialog";
import { useSettings } from "@/store/settings";
import { useSelections } from "@/store/selections";
import { storeToRefs } from "pinia";
import Loading from "vue-loading-overlay";
import { BerwowEvent, TrackingEvent, emit, on } from "@/helpers";
import { useConfirm } from "primevue/useconfirm";
import { useLoan, useRecommendations } from "@/store";
import {
  BerWowSettings,
  FormType,
  ResponseError,
  SelectionButtonsItem,
} from "@/types";
import SelectionsButtons from "@/components/forms/components/SelectionsButtons.vue";
import { FORM_COMPONENT_DICT, FORM_LABEL_DICT } from "@/types/constants";
import { t } from "@/helpers/locale";

export default defineComponent({
  name: "SelectionsPage",
  components: {
    Card,
    Button,
    Loading,
    ConfirmDialog,
    SelectionsButtons,
  },
  setup() {
    const selections = useSelections();
    const recommendations = useRecommendations();
    const loan = useLoan();
    const { settings, byBwid, apiToken } = storeToRefs(useSettings());
    const settingsStore = useSettings();
    const { setByBwId } = settingsStore;
    const { bwid } = storeToRefs(settingsStore);
    const { submitForm, setSubmitLoading, setSelectedFormType } = selections;
    const {
      termsAccepted,
      fileUploaded,
      addressResult,
      submitLoading,
      selectedFormType,
      settingsLoaded,
    } = storeToRefs(selections);

    const selectionsPageLoading = computed(() => {
      return (
        (settings.value && apiToken.value == null) ||
        byBwid.value ||
        submitLoading.value
      );
    });

    const formTypeOptions = computed<SelectionButtonsItem[]>(() => {
      return getFormTypeOptions(
        settings.value?.formOptions?.displayForms as FormType[]
      );
    });

    const selectedForm: WritableComputedRef<FormType | undefined> = computed({
      get() {
        return selectedFormType.value ? selectedFormType.value : undefined;
      },
      set(val: FormType | undefined) {
        setSelectedFormType(val);
      },
    });
    const formComponent = computed(() =>
      selectedForm.value
        ? FORM_COMPONENT_DICT.get(selectedForm.value)
        : undefined
    );

    const userUsesMPRN = computed(() => selectedForm.value === FormType.MPRN);

    on(BerwowEvent.SettingsLoaded, (settings: BerWowSettings) => {
      emit(
        BerwowEvent.TrackingEvent,
        bwid.value
          ? {
              event: TrackingEvent.PageMounted,
              bwid: bwid.value,
              timestamp: Date.now(),
            }
          : {
              event: TrackingEvent.PageMounted,
              timestamp: Date.now(),
            }
      );
      if (settings && settings.formOptions?.formInitialOption != undefined) {
        selectedForm.value =
          formTypeOptions.value[settings.formOptions?.formInitialOption].value;
      }
      settingsLoaded.value = true;
    });

    const addressRequiredAndValid = computed(() =>
      settings.value &&
      settings.value.autoaddressKey &&
      formComponent.value != FORM_COMPONENT_DICT.get(FormType.TypicalHomeUK)
        ? !!addressResult.value
        : true
    );

    const confirm = useConfirm();

    on(BerwowEvent.RecommendationsError, (error: ResponseError) => {
      setSubmitLoading(false);
      showPopup(
        tryParseJSON(error.message) && tryParseJSON(error.message).message
          ? tryParseJSON(error.message).message
          : error.code == "413"
          ? t("selections.uploadMPRN.maxSizeExceeded")
          : error.message
      );
    });

    function getFormTypeOptions(
      displayForms?: FormType[]
    ): SelectionButtonsItem[] {
      const formTypes = displayForms?.length
        ? displayForms
        : [FormType.TypicalHome, FormType.MPRN];

      return formTypes.map((formType) => {
        let keyPrefix = "selections.";

        if (formTypes.includes(FormType.BER)) {
          keyPrefix = "selections.formBerOptions.";
        } else if (formTypes.includes(FormType.TypicalHomeUK)) {
          keyPrefix = "selections.ukForm.";
        } else {
          keyPrefix = "selections.forms.";
        }

        return {
          content: t(`${keyPrefix}${FORM_LABEL_DICT.get(formType)}`) || "",
          value: formType,
        };
      });
    }

    function tryParseJSON(jsonString: string) {
      try {
        return JSON.parse(jsonString);
      } catch (error) {
        console.error(error)
        return null;
      }
    }

    function showPopup(message: string) {
      confirm.require({
        message,
        header: " ",
        icon: "pi pi-times-circle",
        acceptLabel: "Accept",
        rejectClass: "reject-button-hide",
        accept: () => {
          reset();
        },
        onHide: () => {
          reset();
        },
      });
    }

    const reset = () => {
      loan.$reset();
      recommendations.$reset();
      selections.clearMRPNFile();
      setByBwId(false);
    };

    return {
      settings,
      termsAccepted,
      fileUploaded,
      submitForm,
      selectionsPageLoading,
      addressRequiredAndValid,
      settingsLoaded,
      userUsesMPRN,
      selectedForm,
      formTypeOptions,
      formComponent,
    };
  },
});
</script>

<style scoped lang="scss">
.selections-page {
  min-height: 100vh;
  display: flex;
  justify-content: center;

  .selector {
    &:deep(.form-control-buttons) {
      margin-top: 2rem;
    }
  }

  .cards-container {
    width: 80%;
    flex-direction: column;
    align-items: center;
    padding: 2rem;
  }

  .selector:deep(.form-control-radio-buttons) {
    padding: 2rem;

    span {
      display: block;
    }
  }

  .selector:deep(.text) {
    display: block;
    margin-top: 1rem;
    font-size: 0.8rem;
  }

  .selector:deep(.p-button):has(.formBerOpt) {
    align-items: flex-start;
  }

  .submit-button-wrapper {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    padding: 1.5rem;

    .submit-button {
      width: 30%;
      min-width: fit-content;
      height: 3rem;
    }
  }
}

@media screen and (max-width: 1120px) {
  .selections-page {
    .cards-container {
      width: 100%;
      padding: 0.5rem;
    }

    .submit-button {
      width: 50% !important;
      margin-bottom: 2rem;
    }
  }
}

@media screen and (max-width: 720px) {
  .selections-page {
    .cards-container {
      padding: 0.25rem;
    }

    .submit-button {
      width: 100% !important;
      margin-bottom: 2rem;
    }

    .selector:deep(.form-control-radio-buttons) {
      padding: 0;
    }

    .selector:deep(.form-control-buttons):has(.formBerOpt) {
      flex-direction: column;
    }
  }
}
</style>
